import { Component, EventEmitter, Input, OnInit, Output, ViewChild, DoCheck } from '@angular/core';
import {
  IFilterToApply,
  ISearchCriteria,
  SearchSelector,
  ISelectInput,
  FilterContentItem,
  FILTER_CONTENT_ITEM,
} from '@portal/shared/ui/filter';
import {
  addMilliseconds,
  differenceInMilliseconds,
  endOfDay,
  isBefore,
  startOfDay,
} from 'date-fns';
import { ComparableDateService } from './services/date.service';
import { QuickPicks } from './enums/date.enum';
import { IDateFilter } from '@portal/shared/helpers';
import { IQueryParams } from '@portal/shared/ui/table/src';
import { CompareToQuickpicks } from './lists/compate-to-quickpicks.list';
import { DateRangeQuickpicks } from './lists/date-range-quickpicks.list';
import { NgbDate } from '@ng-bootstrap/ng-bootstrap';
import { DateRangeComponent } from './date-range/date-range.component';
import { Observable } from 'rxjs';

declare const $localize;

@Component({
  selector: 'portal-comparable-date-range',
  templateUrl: './comparable-date-range.component.html',
  providers: [
    {
      provide: FILTER_CONTENT_ITEM,
      useExisting: ComparableDateRangeComponent,
    },
  ],
  standalone: false,
})
export class ComparableDateRangeComponent extends FilterContentItem implements OnInit, DoCheck {
  @Output()
  filterApplied: EventEmitter<Map<string, ISearchCriteria>> = new EventEmitter();
  @Output() itemToggled = new EventEmitter();
  @Input()
  isNewDesignFilter = false;
  @Input()
  isCheckboxDisabled: boolean;
  @ViewChild(DateRangeComponent, { static: true })
  dateRangeComponent: DateRangeComponent;
  // Date Range & compare to
  selectedDateRangeQP: QuickPicks = QuickPicks.Last7Days;
  searchCriteriaCompareTo: Map<string, ISearchCriteria> = new Map();
  searchCriteriaDateRange: Map<string, ISearchCriteria> = new Map();
  searchCriteria: Map<string, ISearchCriteria>;
  dateRangeQuickPicksList: ISelectInput[];
  compareToQuickPicksList: ISelectInput[];
  dateRangeSelection: IDateFilter;
  customCompareToSelection: IDateFilter;
  filter: IQueryParams;
  dateRange = $localize`Date range`;
  compareTo = $localize`Compare to`;
  isCompareType = true;
  isCompareToBoxChecked = false;
  isValidDiffLength = true;
  isValidDateRange = true;
  isValidDateRangeFrom = true;
  isValidDateCompareTo = true;
  dateRangeDifferenceInDays: number;
  compareToDifferenceInDays: number;
  isDateRangesOverlapping: boolean;
  isStartDateBiggerThanEndDate: boolean;
  showQuickPicks = true;
  isDisabled: (date: NgbDate) => boolean;
  constructor(
    private comparableDateService: ComparableDateService,
    private compareToQuickpicks: CompareToQuickpicks,
    private dateRangeQuickpicks: DateRangeQuickpicks,
  ) {
    super();
  }

  ngOnInit(): void {
    this.dateRangeQuickPicksList = this.dateRangeQuickpicks.list;
    this.compareToQuickPicksList = this.compareToQuickpicks.list;

    this.initializeFilters();
  }
  ngDoCheck(): void {
    this.isDisabled = (date: NgbDate): boolean => {
      if (this.isCompareToBoxChecked) {
        return this.isCompareToPickerDisabled(date);
      }
      return false;
    };
  }

  clearFilter(): void {
    this.initializeFilters();
  }

  onFilterApplied(): void {
    return null;
  }
  getList(): Observable<any> {
    return null;
  }

  isCompareToPickerDisabled(date: NgbDate): boolean {
    const fromDateCriteria = this.searchCriteriaDateRange.get('dateRange_fromDate');
    const fromDateStr = fromDateCriteria ? fromDateCriteria.argument : null;
    const fromDate = fromDateStr ? new Date(fromDateStr) : null;
    const dateObject = new Date(date.year, date.month - 1, date.day);
    if (!fromDate) {
      return false;
    }
    return dateObject >= fromDate;
  }

  focusSearchBox(): void {
    if (this.dateRangeComponent) {
      this.dateRangeComponent.setAutoFocusState();
    }
  }

  onCompareToQuickPickApplied(value: QuickPicks): void {
    const dateFilter = this.comparableDateService.getCompareToQPStartEndDate(
      value,
      this.selectedDateRangeQP,
      this.dateRangeSelection,
    );
    this.onCustomSelectionCompareTo(dateFilter);

    this.searchCriteriaCompareTo = this.getSearchDateObject(
      dateFilter.start,
      dateFilter.end,
      SearchSelector.CompareTo,
    );
  }

  onDateRangeQuickPickApplied(value: QuickPicks): void {
    this.selectedDateRangeQP = value;

    const dateFilterForDateRange = this.comparableDateService.getDateRangeQPStartEndDate(value);

    this.searchCriteriaDateRange = this.getSearchDateObject(
      dateFilterForDateRange.start,
      dateFilterForDateRange.end,
      SearchSelector.DateRange,
    );

    // Quick Pick methods can't bring overlapping and date range length differences
    this.dateRangeSelection = dateFilterForDateRange;
    this.dateRangeDifferenceInDays = this.getDifferenceInDays(dateFilterForDateRange);
  }

  onCompareToCheckedUpdated(isChecked: boolean): void {
    this.isCompareToBoxChecked = isChecked;
    if (this.isCompareToBoxChecked) {
      this.searchCriteria.set(`${SearchSelector.CompareTo}_${SearchSelector.FromDate}`, {
        argument: this.searchCriteriaCompareTo.get(
          `${SearchSelector.CompareTo}_${SearchSelector.FromDate}`,
        ).argument,
        selector: SearchSelector.FromDate,
        operator: '=',
      });
      this.searchCriteria.set(`${SearchSelector.CompareTo}_${SearchSelector.ToDate}`, {
        argument: this.searchCriteriaCompareTo.get(
          `${SearchSelector.CompareTo}_${SearchSelector.ToDate}`,
        ).argument,
        selector: SearchSelector.ToDate,
        operator: '=',
      });
      this.filterApplied.emit(this.searchCriteria);
    } else {
      this.searchCriteria.delete(`${SearchSelector.CompareTo}_${SearchSelector.FromDate}`);
      this.searchCriteria.delete(`${SearchSelector.CompareTo}_${SearchSelector.ToDate}`);
      this.filterApplied.emit(this.searchCriteria);
    }
  }

  onCompareToFilterApplied(filters?: IFilterToApply[]): void {
    const toDate = filters.find(
      (filter) => filter.key === `${SearchSelector.CompareTo}_${SearchSelector.ToDate}`,
    );
    const endDate = new Date(
      this.searchCriteria.get(`${SearchSelector.DateRange}_${SearchSelector.ToDate}`)?.argument,
    );

    this.updateFilter(filters, this.searchCriteriaCompareTo);

    this.searchCriteria = new Map([...this.searchCriteria, ...this.searchCriteriaCompareTo]);
    this.filterApplied.emit(this.searchCriteria);
  }
  onDateRangeFilterApplied(filters?: IFilterToApply[]): void {
    this.updateFilter(filters, this.searchCriteriaDateRange);

    const dateFilterForCompareTo = this.setCompareToTime(
      this.dateRangeSelection,
      this.comparableDateService.getCompareToPreviousPeriod(
        this.selectedDateRangeQP,
        this.dateRangeSelection,
      ),
    );

    const endDate = new Date(
      this.searchCriteriaDateRange.get(
        `${SearchSelector.DateRange}_${SearchSelector.ToDate}`,
      )?.argument,
    );

    // When endDate is today
    if (isBefore(endDate, endOfDay(endDate))) {
      const difference = differenceInMilliseconds(endDate, startOfDay(endDate));
      dateFilterForCompareTo.end = addMilliseconds(
        startOfDay(new Date(dateFilterForCompareTo.end)),
        difference,
      ).toISOString();
    }

    this.searchCriteriaCompareTo = this.getSearchDateObject(
      dateFilterForCompareTo.start,
      dateFilterForCompareTo.end,
      SearchSelector.CompareTo,
    );

    this.searchCriteria = this.isCompareToBoxChecked
      ? new Map([
          ...this.searchCriteria,
          ...this.searchCriteriaDateRange,
          ...this.searchCriteriaCompareTo,
        ])
      : new Map([...this.searchCriteria, ...this.searchCriteriaDateRange]);

    this.filterApplied.emit(this.searchCriteria);
  }

  onDateFilterToggled(): void {
    this.itemToggled.emit();
  }

  onCustomSelectionFromDateRange(dateFrom: Date): void {
    if (!this.dateRangeSelection) {
      return;
    }
    this.dateRangeSelection.start = dateFrom.toISOString();
    this.isValidDateRangeFrom = !this.isDateBiggerThanCurrentDate(dateFrom);
  }

  isDateBiggerThanCurrentDate(date: Date): boolean {
    return this.comparableDateService.dateIsBiggerThanCurrentDate(
      NgbDate.from({
        day: date.getDate(),
        month: date.getMonth() + 1,
        year: date.getFullYear(),
      }),
    );
  }

  onCustomSelectionFromDateCompareTo(dateFrom: Date): void {
    if (!this.customCompareToSelection) {
      return;
    }
    this.customCompareToSelection.start = startOfDay(dateFrom).toISOString();
    this.customCompareToSelection.end = endOfDay(dateFrom).toISOString();
    // check if CompareTo and DateRange date lengths has differences
    this.compareToDifferenceInDays = this.getDifferenceInDays(this.customCompareToSelection);

    this.isValidDiffLength = this.dateRangeDifferenceInDays === this.compareToDifferenceInDays;

    // check date ranges overlapping
    this.isDateRangesOverlapping = this.getDateRangesOverlappingState();

    this.isValidDateCompareTo =
      this.comparableDateService.checkValidDateRange(this.customCompareToSelection) &&
      !this.isDateBiggerThanCurrentDate(dateFrom);
  }

  onCustomSelectionDateRange(dateSelection: IDateFilter): void {
    this.selectedDateRangeQP = QuickPicks.Custom;
    this.dateRangeSelection = dateSelection;
    this.dateRangeDifferenceInDays = this.getDifferenceInDays(this.dateRangeSelection);
    const dateRangeEnd = new Date(this.dateRangeSelection.end);
    this.isValidDateRange =
      this.comparableDateService.checkValidDateRange(this.dateRangeSelection) &&
      !this.isDateBiggerThanCurrentDate(dateRangeEnd);
  }

  onCustomSelectionCompareTo(dateSelection: IDateFilter): void {
    this.customCompareToSelection = dateSelection;

    // check if CompareTo and DateRange date lengths has differences
    this.compareToDifferenceInDays = this.getDifferenceInDays(this.customCompareToSelection);

    this.isValidDiffLength = this.dateRangeDifferenceInDays === this.compareToDifferenceInDays;

    // check date ranges overlapping
    this.isDateRangesOverlapping = this.getDateRangesOverlappingState();
  }

  private getDifferenceInDays(dateSelection: IDateFilter): number {
    return this.comparableDateService.getDifferenceInDays(dateSelection);
  }

  private getDateRangesOverlappingState(): boolean {
    return this.comparableDateService.getDateRangesOverlappingState(
      this.dateRangeSelection,
      this.customCompareToSelection,
    );
  }

  private getSearchDateObject(
    startDate: string,
    endDate: string,
    keyPrefix?: string,
  ): Map<string, ISearchCriteria> {
    return new Map<string, ISearchCriteria>([
      [
        `${keyPrefix}_${SearchSelector.FromDate}`,
        {
          argument: startDate,
          selector: SearchSelector.FromDate,
          operator: '=',
        },
      ],
      [
        `${keyPrefix}_${SearchSelector.ToDate}`,
        {
          argument: endDate,
          selector: SearchSelector.ToDate,
          operator: '=',
        },
      ],
    ]);
  }

  private updateFilter(
    filters: IFilterToApply[],
    searchCriteria: Map<string, ISearchCriteria>,
  ): void {
    filters.forEach((filter: IFilterToApply) => {
      if (filter) {
        if (filter.toRemove) {
          searchCriteria.delete(filter.key);
        } else {
          searchCriteria.set(filter.key, {
            argument: filter.argument,
            selector: filter.selector,
            operator: filter.operator,
            values: new Set(filter.value.map((item) => item.id)),
          });
        }
      }
    });
  }

  private setCompareToTime(source: IDateFilter, target: IDateFilter): IDateFilter {
    return {
      start: this.setTimeHelper(source.start, target.start),
      end: this.setTimeHelper(source.end, target.end),
    };
  }

  private setTimeHelper(sourceISO: string, targetISO: string): string {
    const sourceDate = new Date(sourceISO);
    const targetDate = new Date(targetISO);
    const updatedTarget = new Date(targetDate);

    updatedTarget.setHours(sourceDate.getHours());
    updatedTarget.setMinutes(sourceDate.getMinutes());
    updatedTarget.setSeconds(sourceDate.getSeconds());
    updatedTarget.setMilliseconds(sourceDate.getMilliseconds());

    return updatedTarget.toISOString();
  }

  private initializeFilters(): void {
    this.isCompareToBoxChecked = false;
    this.searchCriteriaDateRange = this.getSearchDateObject(
      this.comparableDateService.getTodaysDateFilter().start,
      this.comparableDateService.getTodaysDateFilter().end,
      SearchSelector.DateRange,
    );
    this.searchCriteriaCompareTo = this.getSearchDateObject(
      this.comparableDateService.getYesterdaysDateFilter().start,
      this.comparableDateService.getYesterdaysDateFilter().end,
      SearchSelector.CompareTo,
    );

    this.searchCriteria = new Map([
      ...this.searchCriteriaDateRange,
      ...(this.isCompareToBoxChecked ? this.searchCriteriaCompareTo : []),
    ]);
    this.filterApplied.emit(this.searchCriteria);
  }
}
